import apiClient from "../api/api";
import { API_ENDPOINTS } from "../api/api.config";

export enum ConnectorSyncStatus {
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  ERROR  = "ERROR",
}

export interface SheetFile {
  spreadsheet_id: string;
  name: string;
  modified_at: string;
  is_connected: boolean;
  connector_id: string | null;
}

export interface SheetMetadata {
  name: string;
  row_count: number;
}

// Used by single-record CRUD endpoints (GET by id, pause, resume, delete)
export interface SpreadsheetConnector {
  _id: string;
  spreadsheet_id: string;
  spreadsheet_name: string;
  document_store_id: string;
  sync_status: ConnectorSyncStatus;
  last_synced_at: string | null;
  sheets_metadata: SheetMetadata[];
  total_rows: number;
  watch_channel_expiry: string | null;
}

// Shape of each file entry inside a credential group (active-sheets response)
export interface ConnectedFile {
  _id: string;
  spreadsheet_name: string;
  spreadsheet_id: string;
  document_store_id: string;
  sync_status: ConnectorSyncStatus;
  last_synced_at: string | null;
  uploaded_file_id: string | null;
  sheets_metadata: SheetMetadata[];
  total_rows: number;
}

// One credential account with all its connected spreadsheet files
export interface CredentialGroup {
  credential_id: string;
  user_name: string;
  user_email: string;
  document_store_id: string;
  connected_files: ConnectedFile[];
}

export interface Credential {
  _id: string;
  user_name: string;
  user_email: string;
  document_store_id: string;
  createdAt: string;
}

export interface ListCredentialsResult {
  total_count: number;
  data: Credential[];
}

export interface CreateConnectorDto {
  credential_id: string;
  spreadsheet_ids: string[];
  sheet_name?: string;
}

export interface ActiveSheetsParams {
  document_store_id?: string;
  search?: string;
  skip?: number;
  limit?: number;
}

export interface ActiveSheetsResult {
  total_count: number;
  last_modified: string | null;
  credentials: CredentialGroup[];
}

export enum SyncTriggerType {
  INITIAL = "INITIAL",
  WEBHOOK = "WEBHOOK",
  RESUME  = "RESUME",
  MANUAL  = "MANUAL",
}

export enum SyncHistoryStatus {
  SUCCESS = "SUCCESS",
  FAILED  = "FAILED",
  SKIPPED = "SKIPPED",
}

export interface SyncHistoryRecord {
  _id: string;
  connector_id: string;
  document_store_id: string;
  trigger_type: SyncTriggerType;
  trigger_channel_id?: string;
  changed_fields?: string;
  status: SyncHistoryStatus;
  start_time: string;
  end_time?: string;
  time_taken?: number;
  sheets_synced?: string[];
  total_rows?: number;
  rows_changed?: number;
  chunks_reembedded?: number;
  chunks_deleted?: number;
  error?: string;
  createdAt: string;
}

export interface SyncHistoryFilters {
  connector_id?: string;
  document_store_id?: string;
  trigger_type?: SyncTriggerType;
  status?: SyncHistoryStatus;
  from_date?: string;
  to_date?: string;
  skip?: number;
  limit?: number;
}

export interface SyncHistoryResult {
  total_count: number;
  data: SyncHistoryRecord[];
}

class SpreadsheetConnectorService {
  async getAuthUrl(documentStoreId: string): Promise<{ auth_url: string }> {
    const response = await apiClient.get(
      `${API_ENDPOINTS.SPREADSHEET_CONNECTOR.AUTH_GOOGLE}?document_store_id=${documentStoreId}`
    );
    return response.data.data;
  }

  async listCredentials(documentStoreId: string): Promise<ListCredentialsResult> {
    const response = await apiClient.post(API_ENDPOINTS.SPREADSHEET_CONNECTOR.LIST_CREDENTIALS, {
      document_store_id: documentStoreId,
    });
    const result = response.data.data;
    return {
      total_count: result.total_count ?? 0,
      data: result.data ?? [],
    };
  }

  async listSheetFiles(credentialId: string): Promise<SheetFile[]> {
    const response = await apiClient.get(
      API_ENDPOINTS.SPREADSHEET_CONNECTOR.LIST_SHEETS(credentialId)
    );
    return response.data.data;
  }

  async createConnectors(dto: CreateConnectorDto): Promise<SpreadsheetConnector[]> {
    const response = await apiClient.post(API_ENDPOINTS.SPREADSHEET_CONNECTOR.CREATE, dto);
    return response.data.data;
  }

  async getConnectorById(id: string): Promise<SpreadsheetConnector> {
    const response = await apiClient.get(API_ENDPOINTS.SPREADSHEET_CONNECTOR.GET_BY_ID(id));
    return response.data.data;
  }

  async listActiveConnectors(params: ActiveSheetsParams): Promise<ActiveSheetsResult> {
    const response = await apiClient.post(API_ENDPOINTS.SPREADSHEET_CONNECTOR.ACTIVE_SHEETS, params);
    return response.data.data;
  }

  async pauseConnector(id: string): Promise<void> {
    await apiClient.put(API_ENDPOINTS.SPREADSHEET_CONNECTOR.PAUSE(id));
  }

  async resumeConnector(id: string): Promise<void> {
    await apiClient.put(API_ENDPOINTS.SPREADSHEET_CONNECTOR.RESUME(id));
  }

  async deleteConnector(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.SPREADSHEET_CONNECTOR.DELETE(id));
  }

  async listSyncHistory(filters: SyncHistoryFilters): Promise<SyncHistoryResult> {
    const response = await apiClient.post(API_ENDPOINTS.SPREADSHEET_CONNECTOR.SYNC_HISTORY, filters);
    const result = response.data.data;
    return {
      total_count: result.total_count ?? 0,
      data: result.data ?? [],
    };
  }
}

const spreadsheetConnectorService = new SpreadsheetConnectorService();
export default spreadsheetConnectorService;
